import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDiceD20,
  faArrowRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
// Components
import TableLoader from "../../../components/TableLoader";
// Services
import { getWnbaModels } from "../../../services/wnbaModelService";

/* ---- helpers ------------------------------------------------------------ */

const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const cents = (v) => (v === null || v === undefined ? "—" : `${v}c`);

// Implied American price for a win probability given as 0-100. A certainty has
// no price, so those come back empty rather than as a divide by zero.
const american = (v) => {
  if (v === null || v === undefined) return null;
  const p = Number(v) / 100;
  if (!(p > 0) || !(p < 1)) return null;
  const odds = p >= 0.5 ? (-100 * p) / (1 - p) : (100 * (1 - p)) / p;
  const rounded = Math.round(odds);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

// A probability with its American equivalent. Renders as a fragment so the
// caller's own colour and weight still apply to the percentage.
function Prob({ value }) {
  const odds = american(value);
  return (
    <>
      {pct(value)}
      {odds && (
        <span className="ml-1 text-xs font-normal text-slate-400">({odds})</span>
      )}
    </>
  );
}

const signed = (v) =>
  v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(2)}`;

const marketLabel = (m) =>
  m.type === "spreads" ? `Spread ${m.line}` : "Moneyline";

const dayKey = (game) =>
  game.day || moment(game.commence_time).format("YYYY-MM-DD");

const dayLabel = (key) => moment(key, "YYYY-MM-DD").format("ddd, MMM D");

/* ---- day tabs ------------------------------------------------------------ */

function DayTabs({ days, selected, onSelect }) {
  // The active underline sits on the row's own border rather than under it.
  return (
    <div className="mt-4 flex flex-wrap gap-x-7 border-b border-slate-200">
      {days.map((day) => {
        const active = day.key === selected;
        return (
          <button
            key={day.key}
            type="button"
            onClick={() => onSelect(day.key)}
            className={`-mb-px border-b-2 pb-2.5 text-sm font-semibold transition ${
              active
                ? "border-primary text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {dayLabel(day.key)}
          </button>
        );
      })}
    </div>
  );
}

/* ---- one side of one market --------------------------------------------- */

function SideRow({ side }) {
  // Whichever model is lower set the price: that is the one we trust here.
  const jalenSet = side.jalen <= side.trevor;
  const drifted = side.resting !== null && side.resting !== side.ceiling;

  return (
    <tr>
      <td className="font-medium text-slate-800">
        {side.side}
        {side.point !== null && side.point !== undefined && (
          <span className="ml-2 font-mono text-xs text-slate-400">
            {side.point > 0 ? `+${side.point}` : side.point}
          </span>
        )}
      </td>
      <td className={`font-mono ${jalenSet ? "font-semibold text-slate-900" : "text-slate-400"}`}>
        <Prob value={side.jalen} />
      </td>
      <td className={`font-mono ${!jalenSet ? "font-semibold text-slate-900" : "text-slate-400"}`}>
        <Prob value={side.trevor} />
      </td>
      <td className="font-mono font-semibold text-slate-900"><Prob value={side.worst} /></td>
      <td className="font-mono text-base font-semibold text-primary">{cents(side.ceiling)}</td>
      <td className="font-mono text-slate-500">{side.roi === null ? "—" : `${side.roi}%`}</td>
      <td className="font-mono">
        {side.resting === null || side.resting === undefined ? (
          <span className="text-slate-400">not up</span>
        ) : drifted ? (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 ring-1 ring-amber-200">
            {cents(side.resting)}
          </span>
        ) : (
          <span className="text-emerald-600">{cents(side.resting)}</span>
        )}
      </td>
    </tr>
  );
}

/* ---- one market (moneyline or spread) ----------------------------------- */

function MarketBlock({ market }) {
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900">{marketLabel(market)}</h4>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            market.sum < 100
              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
              : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
          }`}
        >
          pair {market.sum}c · edge {market.edge > 0 ? `+${market.edge}` : market.edge}c
        </span>
        {market.blocked && (
          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
            not quoted — {market.blocked}
          </span>
        )}
      </div>

      <table className="mt-2">
        <thead>
          <tr>
            <th>Side</th>
            <th>Jalen</th>
            <th>Trevor</th>
            <th>Worst</th>
            <th>Our ceiling</th>
            <th>ROI</th>
            <th>Resting</th>
          </tr>
        </thead>
        <tbody>
          {market.sides.map((side) => (
            <SideRow key={`${market.type}-${side.side}`} side={side} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- polymarket reference ------------------------------------------------ */

function PolymarketBlock({ rows }) {
  const usable = (rows || []).filter(
    (r) => r.home_buy !== null || r.over_buy !== null
  );
  if (!usable.length) return null;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Polymarket
      </p>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs text-slate-600">
        {usable.map((r, i) => (
          <span key={i}>
            {r.market_type}
            {r.line !== null && r.line !== undefined ? ` ${r.line}` : ""}:{" "}
            {r.market_type === "total"
              ? `over ${r.over_buy ?? "—"} / under ${r.under_buy ?? "—"}`
              : `home ${r.home_buy ?? "—"} / away ${r.away_buy ?? "—"}`}
            {r.captured_at && (
              <span className="text-slate-400">
                {" "}
                ({moment(r.captured_at).format("MMM D HH:mm")})
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---- one game ------------------------------------------------------------ */

function GameCard({ game }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{game.label}</h3>
            {game.listed === false && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                not open on Polymarket yet
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-400">
            {game.listed === false && game.day
              ? `${dayLabel(game.day)} · time TBD`
              : moment(game.commence_time).format("ddd MMM D, HH:mm")}
            {game.series ? ` · ${game.series}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-slate-500">
          {game.jalen && (
            <span>
              Jalen <Prob value={game.jalen.p_home} /> home · margin {signed(game.jalen.margin)}
              {game.jalen.total ? ` · total ${Number(game.jalen.total).toFixed(1)}` : ""}
            </span>
          )}
          {game.trevor && (
            <span>
              Trevor <Prob value={game.trevor.p_home} /> home · margin {signed(game.trevor.margin)}
            </span>
          )}
        </div>
      </div>

      {game.holding && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
          Not quoting this game — {game.holding}
        </p>
      )}

      {game.markets.map((market) => (
        <MarketBlock key={`${market.type}-${market.line}`} market={market} />
      ))}

      {game.spread_note && (
        <p className="mt-3 text-xs text-slate-400">No spread — {game.spread_note}</p>
      )}

      {game.provider_line !== undefined &&
        game.native_line !== undefined &&
        game.provider_line !== game.native_line && (
          <p className="mt-2 text-xs text-slate-400">
            Catalog advertises {game.provider_line}; Polymarket trades{" "}
            {game.native_line}.
          </p>
        )}

      <PolymarketBlock rows={game.polymarket} />
    </div>
  );
}

/* ---- page ---------------------------------------------------------------- */

export default function WnbaModels() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getWnbaModels()
      .then((res) => {
        setBoard(res.data);
        setError(null);
      })
      .catch(() => setError("Could not load the model board."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const games = useMemo(() => board?.games || [], [board]);

  const days = useMemo(() => {
    const byDay = new Map();
    for (const game of games) {
      const key = dayKey(game);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(game);
    }
    return [...byDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, dayGames]) => ({ key, games: dayGames }));
  }, [games]);

  const [picked, setPicked] = useState(null);

  // Default to today when it is on the board, otherwise the next slate. Derived
  // rather than stored, so a day whose games tip off and drop out falls back on
  // its own, and the first render already has a day selected.
  const day = days.some((d) => d.key === picked)
    ? picked
    : (days.find((d) => d.key === moment().format("YYYY-MM-DD")) || days[0])?.key ??
      null;

  const shown = days.find((d) => d.key === day)?.games || [];

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Market making</p>
          <div className="flex items-center gap-3">
            <h1>WNBA Models</h1>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
              <FontAwesomeIcon icon={faDiceD20} className="text-[11px]" />
              Jalen + Trevor
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {board?.generated_at && (
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                board.stale
                  ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                  : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
              }`}
            >
              {board.stale ? "stale — " : "updated "}
              {moment(board.generated_at).fromNow()}
            </span>
          )}
          <button type="button" className="btn-primary" onClick={load}>
            <FontAwesomeIcon icon={faArrowRotateRight} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <hr />

      <p className="my-3 text-xs text-slate-500">
        Every side is priced off whichever of the two models is <strong>worse</strong> for
        that side, at the price still returning {board?.margin ? `${Math.round((board.margin - 1) * 100)}%` : "5%"}{" "}
        ROI against it — {board?.rule || "ceiling = floor(worst / 1.05)"}. Both sides are
        quoted only when the pair sums under 100c, since one of them always wins.
        {board?.margin_sd
          ? ` Spread cover probabilities use a margin error of ${board.margin_sd} points, measured from Jalen's backtest.`
          : ""}
      </p>

      {loading && !board ? (
        <TableLoader />
      ) : error ? (
        <p className="my-4 text-sm font-medium text-rose-600">{error}</p>
      ) : !games.length ? (
        <p className="my-4 text-sm text-slate-500">
          {board?.note || "No upcoming WNBA games."}
        </p>
      ) : (
        <>
          <DayTabs days={days} selected={day} onSelect={setPicked} />
          <div className="mb-3 mt-4 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-slate-900">{dayLabel(day)}</h2>
            <span className="text-xs text-slate-400">
              {shown.length} {shown.length === 1 ? "game" : "games"}
            </span>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4">
            {shown.map((game) => (
              <GameCard key={game.event_id} game={game} />
            ))}
          </div>
        </>
      )}

      {board?.push_table && (
        <div className="my-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">
            What an alternate line is worth
          </h3>
          <p className="mt-1 text-xs text-slate-400">
            How often a WNBA game lands on each margin. Moving a favourite from -3 to
            -3.5 turns those games from a refund into a loss, so the half point costs
            exactly this much. Basketball has no key numbers — it is flat from 2 to 11.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(board.push_table)
              .filter(([k]) => Number(k) >= 1 && Number(k) <= 15)
              .map(([k, v]) => (
                <span
                  key={k}
                  className="rounded-lg border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600"
                >
                  {k}→{Number(k) + 0.5}{" "}
                  <strong className="text-slate-900">{v}%</strong>
                </span>
              ))}
          </div>
        </div>
      )}
    </>
  );
}
